import { Component } from 'react'
import PropTypes from 'prop-types'

import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import { AnnotationType } from '@tabeeb/contracts/main-api/swagger-main-api'
import { contentStateSelectors } from '@tabeeb/shared/content'
import * as playerActions from '@tabeeb/modules/player/actions'
import * as playerSelectors from '@tabeeb/modules/player/selectors'
import * as aiActions from '@tabeeb/modules/artificialIntelligence/actions'
import * as aiSelectors from '@tabeeb/modules/artificialIntelligence/selectors'
import { callApiAsync } from '@tabeeb/modules/shared/utils/requests'
import { UniqueAIObjectCreationMode } from '../enums'

import HotspotDialog from '../components/HotspotDialog'

import * as annotationActions from '../actions'
import * as annotationsSelectors from '../selectors'

class HotspotDialogContainer extends Component {
  annotationUpdateInProgress = false

  constructor(props) {
    super(props)
    this.state = {
      resumeVideoAfterClose: false,
    }
  }

  componentDidMount() {
    const {
      isVideoPlaying,
      resumeVideoAfterClose,
      playerActions: { onPlayerPaused },
    } = this.props

    if (isVideoPlaying) {
      onPlayerPaused()
    }

    this.setState({ resumeVideoAfterClose: isVideoPlaying || resumeVideoAfterClose })
  }

  componentDidUpdate(prevProps, prevState) {
    const { isOpen, childAnnotations } = this.props

    if (isOpen && prevProps.childAnnotations.length === 1 && childAnnotations.length === 0) {
      this._handleClose()
    }
  }

  componentWillUnmount() {
    const { resumeVideoAfterClose } = this.state

    const {
      isVideoPlaying,
      playerActions: { onPlayerPlay },
    } = this.props

    if (resumeVideoAfterClose && !isVideoPlaying) {
      onPlayerPlay()
    }
  }

  _handleClose = (e) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }

    const {
      annotation,
      childAnnotations,
      annotationActions: { closeHotspotDialog, deleteContentAnnotations },
    } = this.props

    closeHotspotDialog()

    if (childAnnotations.length === 0 && !this.annotationUpdateInProgress) {
      if (annotation.ParentAnnotationId) {
        deleteContentAnnotations({ annotationIds: [annotation.ParentAnnotationId] })
      } else {
        deleteContentAnnotations({ annotationIds: [annotation.Id] })
      }
    }
  }

  _handleReply = async (data, annotationType, editAnnotation) => {
    const {
      annotation,
      parentAnnotation,
      contentId,
      annotationActions: { updateContentAnnotation },
    } = this.props

    const handleReplyAsync = async () => {
      if (annotationType === AnnotationType.Text) {
        const model = {
          Color: 'black',
          FontSize: 0,
          PageId: annotation.PageId,
          ParentAnnotationId: annotation.Id,
          Text: data,
          Timestamp: -1,
          Type: annotationType,
        }

        await callApiAsync(annotationActions.addAnnotationRequest(model))

        return
      }

      if (annotationType === AnnotationType.Audio) {
        await callApiAsync(
          annotationActions.addAudioAnnotationRequest({
            contentId,
            pageId: annotation.PageId,
            parentAnnotationId: annotation.Id,
            data,
          })
        )

        return
      }

      if (annotationType === AnnotationType.AI) {
        const { selectedAiObject } = this.props
        const { newUniqueAiObjectName, selectedUniqueAiObject, uniqueAIObjectCreationMode } = data

        // just AIObject
        if (!selectedUniqueAiObject && !newUniqueAiObjectName) {
          const annotationText = `CV Class: ${selectedAiObject.Name}`

          const model = {
            Color: 'black',
            FontSize: 0,
            PageId: annotation.PageId,
            ParentAnnotationId: annotation.Id,
            Text: annotationText,
            Timestamp: -1,
            Type: annotationType,
            AIObjectId: selectedAiObject.Id,
          }

          if (!editAnnotation) {
            await callApiAsync(annotationActions.addAnnotationRequest(model))
          } else {
            model.Id = editAnnotation.Id
            model.UserId = editAnnotation.UserId
            updateContentAnnotation({ annotation: model })

            updateContentAnnotation({ annotation: { ...annotation, Color: selectedAiObject.Color } })
            if (parentAnnotation) {
              updateContentAnnotation({ annotation: { ...parentAnnotation, Color: selectedAiObject.Color } })
            }
          }

          return
        }

        // create new uniqueAIObject
        if (uniqueAIObjectCreationMode === UniqueAIObjectCreationMode.CreateNew && newUniqueAiObjectName) {
          const model = {
            name: newUniqueAiObjectName,
            contentId,
            aiObjectId: selectedAiObject.Id,

            parentAnnotationId: annotation.Id,
            annotationId: null,
          }

          if (editAnnotation) {
            model.annotationId = editAnnotation.Id
          }

          await callApiAsync(aiActions.addUniqueAiObject.request(model))

          updateContentAnnotation({ annotation: { ...annotation, Color: selectedAiObject.Color } })
          if (parentAnnotation) {
            updateContentAnnotation({ annotation: { ...parentAnnotation, Color: selectedAiObject.Color } })
          }
          return
        }

        // create new uniqueAIObject from existing
        if (
          uniqueAIObjectCreationMode === UniqueAIObjectCreationMode.CreateNewFromExisting &&
          newUniqueAiObjectName &&
          selectedUniqueAiObject
        ) {
          const model = {
            name: newUniqueAiObjectName,
            contentId,
            uniqueAIObjectId: selectedUniqueAiObject.Id,

            parentAnnotationId: annotation.Id,
            annotationId: null,
          }

          if (editAnnotation) {
            model.annotationId = editAnnotation.Id
          }

          await callApiAsync(aiActions.shallowCloneUniqueAiObject.request(model))

          updateContentAnnotation({ annotation: { ...annotation, Color: selectedAiObject.Color } })
          if (parentAnnotation) {
            updateContentAnnotation({ annotation: { ...parentAnnotation, Color: selectedAiObject.Color } })
          }
          return
        }

        // selected existing uniqueAIObject
        if (uniqueAIObjectCreationMode === UniqueAIObjectCreationMode.SelectExisting && selectedUniqueAiObject) {
          const annotationText = `CV Class: ${selectedAiObject.Name}, Object: ${selectedUniqueAiObject.Name}`

          const model = {
            Color: 'black',
            FontSize: 0,
            PageId: annotation.PageId,
            ParentAnnotationId: annotation.Id,
            Text: annotationText,
            Timestamp: -1,
            Type: annotationType,
            AIObjectId: selectedAiObject.Id,
            UniqueAIObjectId: selectedUniqueAiObject.Id,
          }

          if (!editAnnotation) {
            await callApiAsync(annotationActions.addAnnotationRequest(model))
          } else {
            model.Id = editAnnotation.Id
            model.UserId = editAnnotation.UserId
            updateContentAnnotation({ annotation: model })

            updateContentAnnotation({ annotation: { ...annotation, Color: selectedAiObject.Color } })
            if (parentAnnotation) {
              updateContentAnnotation({ annotation: { ...parentAnnotation, Color: selectedAiObject.Color } })
            }
          }
        }
      }
    }

    this.annotationUpdateInProgress = true

    await handleReplyAsync()

    this.annotationUpdateInProgress = false
  }

  render() {
    const { isOpen, annotation, x, y, childAnnotations } = this.props
    if (!isOpen) {
      return null
    }

    const props = {
      annotation,
      x,
      y,
      childAnnotations,
      handleClose: this._handleClose,
      handleReply: this._handleReply,
    }

    return <HotspotDialog {...props} />
  }
}

HotspotDialogContainer.propTypes = {
  resumeVideoAfterClose: PropTypes.bool.isRequired,
  parentAnnotation: PropTypes.shape({}),
  childAnnotations: PropTypes.arrayOf(PropTypes.shape({})),
  contentId: PropTypes.number.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isVideoPlaying: PropTypes.bool.isRequired,
  annotation: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    PageId: PropTypes.number.isRequired,
    ParentAnnotationId: PropTypes.number,
  }),
  selectedAiObject: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
    Color: PropTypes.string.isRequired,
    ParentAnnotationId: PropTypes.number,
  }),
  annotationActions: PropTypes.shape({
    updateContentAnnotation: PropTypes.func.isRequired,
    closeHotspotDialog: PropTypes.func.isRequired,
    deleteContentAnnotations: PropTypes.func.isRequired,
  }).isRequired,
  playerActions: PropTypes.shape({
    onPlayerPlay: PropTypes.func.isRequired,
    onPlayerPaused: PropTypes.func.isRequired,
  }).isRequired,
}

const mapStateToProps = (state, { Id }) => {
  const isOpen = annotationsSelectors.getHotspotDialogAnnotationId(state) === Id
  if (!isOpen) {
    return { isOpen }
  }

  const resumeVideoAfterClose = annotationsSelectors.getHotspotDialogVideoResume(state)

  const contentId = contentStateSelectors.getContentId(state)

  const annotation = annotationsSelectors.getAnnotationById(state, { Id })
  const parentAnnotation = annotationsSelectors.getAnnotationById(state, { Id: annotation.ParentAnnotationId })
  const childAnnotations = annotationsSelectors.getAnnotationChildren(state, { Id })

  const selectedAiObject = aiSelectors.getSelectedAiObject(state)

  const isVideoPlaying = playerSelectors.getIsVideoPlaying(state)

  return {
    isOpen,
    isVideoPlaying,
    contentId,
    annotation,
    parentAnnotation,
    childAnnotations,
    selectedAiObject,
    resumeVideoAfterClose,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    annotationActions: bindActionCreators(annotationActions, dispatch),
    playerActions: bindActionCreators(playerActions, dispatch),
    addUniqueAiObject: bindActionCreators(aiActions.addUniqueAiObject, dispatch),
    shallowCloneUniqueAiObject: bindActionCreators(aiActions.shallowCloneUniqueAiObject, dispatch),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(HotspotDialogContainer)
